/**
 * Utility per processare e validare file immagine
 */

import i18n from '../../../config/i18n';
import { formatFileSize, isFileTooLarge, MAX_FILE_SIZE_MB } from '../../../config/upload';
import { isHeicFile } from '../../../utils/heic/detector';
import { processImageFile } from '../../../utils/image-processor';

interface ProcessedPhoto {
  file: File;
  previewUrl: string;
}

interface PhotoProcessingError {
  fileName: string;
  message: string;
}

type PhotoProcessingResult =
  | { success: true; photo: ProcessedPhoto }
  | { success: false; error: PhotoProcessingError };

export async function processPhoto(file: File): Promise<PhotoProcessingResult> {
  try {
    let processedFile = file;

    if (isHeicFile(file)) {
      processedFile = await processImageFile(file);
    }

    if (isFileTooLarge(processedFile)) {
      return {
        success: false,
        error: {
          fileName: file.name,
          message: i18n.t('common.fileErrors.fileTooLarge', {
            fileName: processedFile.name,
            fileSize: formatFileSize(processedFile.size),
            maxSize: MAX_FILE_SIZE_MB,
          }),
        },
      };
    }

    const previewUrl = URL.createObjectURL(processedFile);

    return {
      success: true,
      photo: {
        file: processedFile,
        previewUrl,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: {
        fileName: file.name,
        message:
          error instanceof Error ? error.message : i18n.t('common.fileErrors.conversionError'),
      },
    };
  }
}

export function requiresConversion(file: File): boolean {
  return isHeicFile(file);
}

export function revokePreviewUrl(url: string): void {
  if (url) {
    URL.revokeObjectURL(url);
  }
}

export function getFilesKey(files: File[]): string {
  return files.map((file) => `${file.name}-${file.size}-${file.lastModified}`).join('|');
}
